import { useEffect, useState } from 'react';
import { EFirebaseContext } from '../../types';
import {
  getAffidavitSettingsForNoticeData,
  NoticeWithAffidavitData
} from '../../pricing/affidavits';
import { AffidavitReconciliationSettings } from '../../types/organization';
import { getErrorReporter } from '../../utils/errors';
import { ColumnService } from '../../services/directory';
import { safeAsync } from '../../safeWrappers';

export type NoticeDataAffidavitSettingsResult = {
  /**
   * The cascaded ARS settings, undefined when the newspaper is not in the program.
   */
  settings: AffidavitReconciliationSettings | undefined;
  /**
   * True until the cascade for the current inputs has resolved
   */
  loading: boolean;
  /**
   * The cascade could not be read, so `settings` says nothing about the notice.
   */
  error: boolean;
};

/**
 * Resolves the cascaded affidavit reconciliation settings for a partial notice
 * data object. Use this hook during placement (before a notice snapshot exists)
 * in place of `useNoticeAffidavitSettings`, which requires a saved `ESnapshot<ENotice>`.
 */
export const useNoticeDataAffidavitSettings = (
  ctx: EFirebaseContext,
  noticeData: NoticeWithAffidavitData | undefined
): NoticeDataAffidavitSettingsResult => {
  const newspaperRef = noticeData?.newspaper;
  const filerRef = noticeData?.filer;
  const filedByRef = noticeData?.filedBy;
  const noticeType = noticeData?.noticeType;
  const previousNoticeType = noticeData?.previousNoticeType;

  // Key of every field that affects ARS resolution; the stored result is only
  // valid while it matches the current key.
  const settingsKey = [
    newspaperRef?.id,
    filerRef?.id,
    filedByRef?.id,
    noticeType,
    previousNoticeType
  ].join('|');

  const [resolved, setResolved] = useState<{
    key: string;
    settings: AffidavitReconciliationSettings | undefined;
    failed: boolean;
  } | null>(null);

  useEffect(() => {
    if (!noticeData || !newspaperRef) {
      return;
    }
    // Re-bind after the guard so the async closure keeps the narrowed type.
    const currentNoticeData = noticeData;

    let cancelled = false;

    async function fetchAffidavitSettings() {
      const [err, settings] = await safeAsync(() =>
        getAffidavitSettingsForNoticeData(ctx, currentNoticeData)
      )();
      if (cancelled) {
        return;
      }
      if (err) {
        getErrorReporter().logAndCaptureError(
          ColumnService.WEB_PLACEMENT,
          err,
          'Error fetching affidavit reconciliation settings',
          {
            newspaperRef: newspaperRef?.path || 'undefined',
            filerRef: filerRef?.path || 'undefined',
            filedByRef: filedByRef?.path || 'undefined',
            previousNoticeType: String(previousNoticeType) ?? 'undefined'
          }
        );
        setResolved({ key: settingsKey, settings: undefined, failed: true });
        return;
      }
      setResolved({ key: settingsKey, settings, failed: false });
    }

    void fetchAffidavitSettings();

    return () => {
      cancelled = true;
    };
  }, [settingsKey]);

  // Nothing to resolve without a newspaper; otherwise loading until the stored
  // result was produced for the current inputs.
  const hasFetchableContext = !!noticeData && !!newspaperRef;
  const isCurrent = resolved?.key === settingsKey;

  return {
    settings: isCurrent ? resolved?.settings : undefined,
    loading: hasFetchableContext && !isCurrent,
    error: isCurrent && !!resolved?.failed
  };
};
